import { ref } from 'vue';
import { io } from 'socket.io-client';
import { announceAccountChange, blockAccountPersistence, removeAccountItem, storedAccountId } from './accountStorage';
import { lireDesktopInstalle } from './desktopUpdate';
import { revoquerAutorisationPortail } from './portail';
import { canonicalPublicAssetPayload, resolvePublicAssetUrls } from '../shared/publicAssets';

// Dans l'APK Capacitor la page est servie depuis https://localhost : il n'y a pas
// d'origine serveur à deviner. L'adresse du PC étant variable d'un joueur à
// l'autre, elle est résolue au démarrage (découverte réseau ou saisie) par
// resolveServerUrl(), qui la dépose ici avant le montage de l'application.
// Voir src/store/serverConfig.js et src/main.js.
//
// En dev : accès direct au serveur QUAND la page est sur localhost (comportement
// éprouvé). Depuis un téléphone du réseau local (ou en HTTPS), « localhost »
// désignerait le téléphone lui-même et un appel http:// depuis une page https://
// serait bloqué (mixed content) → on passe alors par le PROXY Vite (même
// origine), qui relaie /socket.io vers le serveur de jeu.
const onLocalhost = typeof location !== 'undefined'
  && ['localhost', '127.0.0.1'].includes(location.hostname);
const viteEnv = (import.meta as any).env || {};
const socketUrl = (globalThis as any).__GUILD_SERVER_URL__
  || viteEnv.VITE_SERVER_URL
  || (viteEnv.DEV && onLocalhost && location.protocol !== 'https:'
    ? 'http://localhost:3000'
    : undefined);
export const socket = io(socketUrl, {
  // Le graphe Vue est chargé statiquement et de façon cohérente par Vite.
  // La connexion attend cependant que main.ts ait résolu l'adresse finale,
  // indispensable dans l'APK où le serveur vit sur le PC du joueur.
  autoConnect: false,
  reconnection: true,
  reconnectionAttempts: 10,
  transports: ['websocket', 'polling'],
});

// Les evenements Socket.io arrivent avant leurs ecouteurs nommes : ce hook
// hydrate donc une seule fois tous les DTO diffuses (SOS, invitations, chat,
// monstres mutualises...). Les acquittements ne passent pas par onAny ; le
// wrapper emit ci-dessous hydrate aussi chaque reponse de callback.
socket.prependAny((_event, ...payloads) => {
  resolvePublicAssetUrls(payloads);
});

const rawSocketEmit = socket.emit.bind(socket);
(socket as any).emit = (event: string, ...args: any[]) => {
  const canonicalArgs = args.map((arg) => {
    if (typeof arg !== 'function') return canonicalPublicAssetPayload(arg);
    const wrappedAcknowledgement = function (this: unknown, ...responseArgs: any[]) {
      resolvePublicAssetUrls(responseArgs);
      return arg.apply(this, responseArgs);
    };
    // emitWithAck() s'appuie sur ce marqueur pour ajouter `null` devant une
    // reponse reussie. Le perdre transformerait toute resolution en rejet.
    if ((arg as any).withError) (wrappedAcknowledgement as any).withError = true;
    return wrappedAcknowledgement;
  });
  return (rawSocketEmit as any)(event, ...canonicalArgs);
};

/** Configure l'adresse finale, annonce le vrai build du conteneur et se connecte. */
export async function connectSocket(resolvedUrl?: string) {
  if (resolvedUrl) socket.io.uri = resolvedUrl;
  const desktop = await lireDesktopInstalle();
  const buildClient = desktop && !desktop.development
    ? desktop.build
    : Number(__APP_BUILD__) || 0;
  // Signal de compatibilité pour le serveur, pas preuve cryptographique : le
  // JavaScript web reste contrôlé par l'utilisateur. Le serveur borne néanmoins
  // cette valeur et valide autoritairement toutes les actions métier.
  socket.auth = { buildClient };
  if (!socket.active) socket.connect();
}

const CLIENT_PRESENCE_HEARTBEAT_MS = 15_000;
let presenceInstalled = false;
let presenceTimer: ReturnType<typeof setInterval> | undefined;
let lastInteractionAt = Date.now();
let lastInteractionEmitAt = 0;
let currentRoute = () => '';

function emitClientPresence(interaction = false) {
  if (!socket.connected) return;
  socket.emit('clientPresence', {
    foreground: !document.hidden,
    interaction,
    lastInteractionAt,
    route: currentRoute().slice(0, 240),
  });
}

export function refreshClientPresence() {
  emitClientPresence(false);
}

/**
 * Signale au serveur si l'application est réellement au premier plan.
 * Une socket Android peut survivre quelque temps à la mise en veille : le
 * heartbeat empêche donc de confondre « encore connecté » et « en train de
 * jouer ». L'état expirera côté serveur si la WebView est suspendue avant
 * d'avoir pu envoyer visibilitychange/pagehide.
 */
export function installClientPresence(routeProvider?: () => string) {
  if (routeProvider) currentRoute = routeProvider;
  if (presenceInstalled) return;
  presenceInstalled = true;

  const onVisibility = () => emitClientPresence(false);
  const onPageHide = () => {
    if (socket.connected) socket.emit('clientPresence', {
      foreground: false,
      interaction: false,
      lastInteractionAt,
      route: currentRoute().slice(0, 240),
    });
  };
  const onInteraction = () => {
    lastInteractionAt = Date.now();
    if (lastInteractionAt - lastInteractionEmitAt < 3_000) return;
    lastInteractionEmitAt = lastInteractionAt;
    emitClientPresence(true);
  };

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onPageHide);
  window.addEventListener('pointerdown', onInteraction, { passive: true });
  window.addEventListener('keydown', onInteraction, { passive: true });
  socket.on('connect', () => emitClientPresence(false));
  presenceTimer = setInterval(() => {
    if (!document.hidden) emitClientPresence(false);
  }, CLIENT_PRESENCE_HEARTBEAT_MS);
  emitClientPresence(false);
}

export const serverOnline = ref(false);

socket.on('connect', () => { serverOnline.value = true; });
socket.on('disconnect', () => { serverOnline.value = false; });
socket.on('connect_error', (error: any) => {
  const code = String(error?.data?.code || '');
  if (!['MISE_A_JOUR_REQUISE', 'MAINTENANCE', 'CLIENT_INCOMPATIBLE'].includes(code)) return;
  // La politique a pu changer entre le passage du Portail et le handshake.
  // La révocation doit précéder le rechargement : si le réseau tombe
  // pendant celui-ci, l'ancienne grâce ne doit jamais rouvrir le jeu.
  revoquerAutorisationPortail();
  window.location.reload();
});
socket.on('portailRefuse', () => {
  // Le serveur relit sa politique sans redémarrer : une session déjà ouverte
  // peut donc devenir interdite. Le rechargement repasse par le Portail avant
  // qu'un autre composant du jeu puisse tenter de se reconnecter.
  revoquerAutorisationPortail();
  window.location.reload();
});
socket.on('forceDisconnect', (data) => {
  window.dispatchEvent(new CustomEvent('guild-toast', { detail: { msg: data?.reason || 'Déconnecté par le serveur.', type: 'error' } }));
  const accountId = String(data?.userId || storedAccountId() || '');
  // Ne jamais vider tout le stockage de l'origine : plusieurs comptes peuvent
  // avoir une sauvegarde locale sur cet appareil. Une revocation de session ou
  // un changement de mot de passe ne touche a aucune progression. Une vraie
  // suppression retire seulement les caches du compte vise.
  if (data?.accountDeleted && accountId) {
    blockAccountPersistence(accountId);
    for (const baseKey of [
      'guild_state_v2',
      'guild-carnet-v1',
      'guild_codex_progress',
      'guild_nemesis_data',
      'guild_marque_aventurier',
    ]) {
      removeAccountItem(baseKey, accountId);
      // La copie globale des anciennes versions n'indique pas son propriétaire.
      // Elle reste donc en quarantaine au lieu de risquer d'effacer les données
      // historiques d'un autre compte utilisé sur cet appareil.
    }
    announceAccountChange('', accountId);
  }
  if (data?.allAccountsDeleted) {
    if (accountId) blockAccountPersistence(accountId);
    try { localStorage.clear(); } catch {}
  } else {
    for (const key of [
      'guild_deviceId',
      'guild_accountEmail',
      'guild_accountRoles',
      'guild_sessionToken',
      'guild_mandataryProfile',
    ]) {
      try { localStorage.removeItem(key); } catch {}
    }
  }
  try { sessionStorage.clear(); } catch {}
  setTimeout(() => { window.location.href = '/intro'; }, 1500);
});
